// Inventory page logic shared by the current page (v1) and the redesigned
// page (v2), so the two cannot drift apart: sorting and paging, the active
// filter pills, the multi-location filter, the URL params the page reads,
// the KPI and "Needs attention" counts.

import { compareByAiPriceGap } from './aiPrice';
import {
    formatYearRange, hasYearFilter, modelDisplayLabel, unitMachine,
} from './machineFilters';
import { ALL_VERDICTS, ANOMALY_GROUPS, countVerdicts } from './anomalySignals';
import { verdictLabel } from './formatting';
import {
    DEFAULT_AGING, LEGACY_AGING_LABELS, OLDEST_BUCKET_ID, agingIdOf, normalizeAgingSelection, unitAgingBucketId,
} from './dealershipSettings';
import { DEAL_STATUS_SORT_KEY, dealStatusRank } from './dealStatus';
import { isCommittedUnit } from './committedUnit';

// ── Sorting and paging ───────────────────────────────────────────────────

// Computed sort key for the AI Price column: the asking vs AI gap %.
export const AI_GAP_SORT_KEY = 'AiPriceGap';
export const DEFAULT_SORT = Object.freeze({ key: 'DaysInStock', direction: 'desc' });
export const GAP_SORT = Object.freeze({ key: AI_GAP_SORT_KEY, direction: 'desc' });

// The sortable columns and the direction of their FIRST click (today's
// table: ascending, except AI Price and Risk, which start descending).
export const SORT_COLUMNS = Object.freeze({
    StockNumber: 'asc',
    MAKE: 'asc',
    BranchName: 'asc',
    DaysInStock: 'asc',
    Best_Sell_Price: 'asc',
    [AI_GAP_SORT_KEY]: 'desc',
    CarryingCostBasis: 'asc',
    LadenhueterRisk: 'desc',
    QuoteCount: 'asc',
    LastQuotedGapPct: 'asc',
    // v2 only (columns picker): Hours, and Deal status by precedence
    // (the most committed first).
    ODOMETER: 'asc',
    [DEAL_STATUS_SORT_KEY]: 'desc',
});

// The sort after clicking `key`: the same column toggles, a new column
// starts in its default direction.
export const nextSort = (current, key, defaultDirection = SORT_COLUMNS[key] || 'asc') => {
    if (current && current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    }
    return { key, direction: defaultDirection };
};

export const isSameSort = (a, b) => Boolean(a && b && a.key === b.key && a.direction === b.direction);

// The sort key of the Risk column: a committed unit has no sell-speed risk.
const RISK_SORT_KEY = 'LadenhueterRisk';

// Nulls go last; strings compare case-insensitively; the AI gap has its own
// comparator (units without a gap last in both directions); the deal status
// sorts by precedence (units without a shown status last). Sorting by Risk
// puts committed units last, after unscored units in stock (R2-B1).
export const sortInventory = (data, sortConfig) => {
    if (!sortConfig || !sortConfig.key) return data;
    if (sortConfig.key === AI_GAP_SORT_KEY) {
        return [...data].sort(compareByAiPriceGap(sortConfig.direction));
    }
    const valueOf = sortConfig.key === DEAL_STATUS_SORT_KEY
        ? (u) => dealStatusRank(u) || null
        : (u) => u[sortConfig.key];
    const committedLast = sortConfig.key === RISK_SORT_KEY;
    return [...data].sort((a, b) => {
        if (committedLast && isCommittedUnit(a) !== isCommittedUnit(b)) return isCommittedUnit(a) ? 1 : -1;
        let aVal = valueOf(a);
        let bVal = valueOf(b);
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = (bVal || '').toLowerCase(); }
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });
};

// Page buttons: all pages up to 7, else 1 … around the current … last.
export const pageNumbers = (page, totalPages) => {
    const pages = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
        pages.push(1);
        if (page > 3) pages.push('...');
        for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
        if (page < totalPages - 2) pages.push('...');
        pages.push(totalPages);
    }
    return pages;
};

export const PAGE_SIZES = [25, 50, 100];

// ── Equipment name ───────────────────────────────────────────────────────

// A unit's model year, or null when it has none: Equip stores a missing
// year as 0 (and some rows as null), which must never render as "0".
export const unitYear = (unit) => {
    const y = Number(unit?.Model_Year);
    return Number.isFinite(y) && y > 0 ? y : null;
};

// "John Deere C16F" for MAKE "JOHN DEERE": the make in the Machine filter's
// display case (its most common mixed-case spelling, else smart title case),
// the model with its plain all-caps words title-cased ("MDS BUCKET" →
// "MDS Bucket"; codes such as S790 stay). `original` is the raw
// "MAKE MODEL" for the title.
export const unitDisplayName = (unit, catalog) => {
    const rawMake = String(unit?.MAKE ?? '').trim();
    const rawModel = String(unit?.MODEL ?? '').trim();
    const original = `${rawMake} ${rawModel}`.trim();
    const makeLabel = rawMake ? (catalog?.makes.get(unitMachine(unit).makeKey)?.label || rawMake) : '';
    const name = [makeLabel, modelDisplayLabel(rawModel)].filter(Boolean).join(' ');
    return { name: name || original, original };
};

// ── Location (single today, multi-select in v2) ──────────────────────────
// The `location` filter is 'All', one branch name (exactly today's shape, so
// saved prefs and ?branch=X are unchanged) or an array of two or more names
// (only the v2 multi-select and ?branch=A,B produce that).

export const unitBranch = (unit) => unit.BranchName ?? unit.LOCATION;

export const locationList = (value) => {
    if (Array.isArray(value)) return value.filter((v) => typeof v === 'string' && v);
    return typeof value === 'string' && value && value !== 'All' ? [value] : [];
};

export const toLocationValue = (list) => {
    const names = [...new Set(list)];
    if (names.length === 0) return 'All';
    if (names.length === 1) return names[0];
    return names;
};

export const matchesLocation = (unit, location) => {
    const names = locationList(location);
    return names.length === 0 || names.includes(unitBranch(unit));
};

const summarise = (labels, noun) => (labels.length > 3 ? `${labels.length} ${noun}` : labels.join(', '));

export const formatLocations = (location) => summarise(locationList(location), 'locations');

// ?branch=all: every location (links from the Branches page, D14).
export const ALL_BRANCHES_PARAM = 'all';

// ?branch= → a location filter value, or null when nothing matches.
// The whole value is matched first, case-insensitively on BranchName (today);
// with `multi`, a comma-separated list selects every branch it names.
export const resolveBranchParam = (param, units, { multi = false } = {}) => {
    if (!param) return null;
    const find = (name) => units.find(
        (u) => (u.BranchName || '').toLowerCase() === name.toLowerCase(),
    )?.BranchName;
    const whole = find(param);
    if (whole) return whole;
    if (!multi) return null;
    // ?branch=all (the Branches page with every branch selected): every
    // location, explicitly, so saved prefs cannot bring back an old one.
    if (param.trim().toLowerCase() === ALL_BRANCHES_PARAM) return 'All';
    const names = param.split(',').map((s) => s.trim()).filter(Boolean).map(find).filter(Boolean);
    return names.length ? toLocationValue(names) : null;
};

// Does the location filter still say what ?branch= says? (If not, the param
// is dropped so a reload does not bring it back.)
export const branchParamMatches = (location, param, { multi = false } = {}) => {
    if (!multi) return (typeof location === 'string' ? location : '').toLowerCase() === param.toLowerCase();
    const have = locationList(location).map((n) => n.toLowerCase()).sort();
    if (have.length === 1 && have[0] === param.toLowerCase()) return true;
    if (param.trim().toLowerCase() === ALL_BRANCHES_PARAM) return have.length === 0;
    const want = [...new Set(param.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean))].sort();
    return have.length === want.length && have.every((n, i) => n === want[i]);
};

// ── Filter predicates shared with hooks/useInventoryFilters ──────────────

// Retarget: an active quote within 10% of asking, or at/above the AI price.
export const matchesRetarget = (u) => u.ActiveQuoteCount > 0 && (
    (u.LastQuotedGapPct != null && u.LastQuotedGapPct >= -10) || u.HasQualifyingQuote
);

export const countQualifyingQuotes = (units) => units.filter((u) => u.HasQualifyingQuote).length;
export const countRetarget = (units) => units.filter(matchesRetarget).length;

// ── Active filter pills ──────────────────────────────────────────────────

export const RISK_TIER_LABELS = Object.freeze({ Red: 'High', Yellow: 'Medium', Green: 'Low' });
export const MACHINE_PILL_FIELDS = Object.freeze(['makes', 'models', 'year']);
// Not counted in the Filters badge: the always-visible controls.
const PRIMARY_PILL_FIELDS = ['location', 'category', 'search', ...MACHINE_PILL_FIELDS];

// One pill per active filter, in today's order. No pills for Live Quote,
// Retarget, Open Tasks or the anomaly selection (they show as toggles).
// `buckets`: the dealership's aging buckets (labels for the Aging pill).
export const buildFilterPills = (filters, machineCatalog, buckets = DEFAULT_AGING.buckets) => {
    const pills = [];
    const aging = normalizeAgingSelection(filters.agingBucket, buckets)
        .map((id) => buckets.find((b) => b.id === id)?.label)
        .filter(Boolean);
    if (locationList(filters.location).length > 0) {
        pills.push({
            field: 'location',
            label: 'Location',
            value: typeof filters.location === 'string' ? filters.location : formatLocations(filters.location),
        });
    }
    if (filters.category !== 'All') pills.push({ field: 'category', label: 'Category', value: filters.category });
    if (filters.newUsed !== 'All') pills.push({ field: 'newUsed', label: 'Type', value: filters.newUsed });
    if (aging.length > 0) pills.push({ field: 'agingBucket', label: 'Aging', value: aging.join(', ') });
    if (filters.riskTier && filters.riskTier !== 'All') {
        pills.push({ field: 'riskTier', label: 'Sell-Speed Risk', value: RISK_TIER_LABELS[filters.riskTier] || filters.riskTier });
    }
    if (filters.sellSpeed && filters.sellSpeed !== 'All') pills.push({ field: 'sellSpeed', label: 'Sell Speed', value: filters.sellSpeed });
    if (filters.buyerHeat && filters.buyerHeat !== 'All') pills.push({ field: 'buyerHeat', label: 'Interest', value: filters.buyerHeat });
    if (filters.salesman !== 'All') pills.push({ field: 'salesman', label: 'Salesman', value: filters.salesman });
    if (filters.search) pills.push({ field: 'search', label: 'Search', value: `"${filters.search}"` });

    const makes = filters.makes || [];
    const models = filters.models || [];
    if (makes.length > 0) {
        const labels = makes.map((key) => machineCatalog?.makes.get(key)?.label || key);
        pills.push({ field: 'makes', label: 'Make', value: summarise(labels, 'makes') });
    }
    if (models.length > 0) {
        const labels = [...new Set(models.map((id) => machineCatalog?.models.get(id)?.label || id))];
        pills.push({ field: 'models', label: 'Model', value: summarise(labels, 'models') });
    }
    if (hasYearFilter(filters)) pills.push({ field: 'year', label: 'Year', value: formatYearRange(filters) });
    return pills;
};

// The Filters button badge: active Type, Aging, Sell-Speed Risk, Sell Speed,
// Interest and Salesman filters.
export const secondaryFilterCount = (pills) => pills.filter((p) => !PRIMARY_PILL_FIELDS.includes(p.field)).length;

// The filters with one pill's filter back at its default.
export const clearFilterField = (filters, field) => {
    if (field === 'search') return { ...filters, search: '' };
    if (field === 'agingBucket') return { ...filters, agingBucket: [] };
    if (field === 'makes' || field === 'models') return { ...filters, [field]: [] };
    if (field === 'year') return { ...filters, yearFrom: null, yearTo: null, includeNoYear: true };
    return { ...filters, [field]: 'All' };
};

// ── KPI band ─────────────────────────────────────────────────────────────

// Aging: the filter holds bucket ids 1–4 (dealership settings name and cut
// the buckets). The legacy default labels, as saved filters may still hold.
export const AGING_BUCKET_NAMES = Object.freeze(Object.keys(LEGACY_AGING_LABELS));
export const OLDEST_BUCKET = OLDEST_BUCKET_ID;

// RiskTier values come from the backend ("Red" | "Yellow" | "Green").
export const RISK_TIER_KEYS = Object.freeze(['Red', 'Yellow', 'Green']);

export const computeRiskStats = (data, tiers = RISK_TIER_KEYS.map((key) => ({ key, label: RISK_TIER_LABELS[key] }))) => {
    const scored = data.filter((u) => u.RiskTier);
    const total = scored.length;
    const counted = tiers.map((t) => {
        const count = scored.filter((u) => u.RiskTier === t.key).length;
        return { ...t, count, pct: total > 0 ? ((count / total) * 100).toFixed(0) : '0' };
    });
    const highCount = counted.find((t) => t.key === 'Red')?.count || 0;
    return { total, tiers: counted, highCount };
};

// ── Needs attention (whole lot, decision D3) ─────────────────────────────

export const UNIT_ISSUE_VERDICTS = ANOMALY_GROUPS.find((g) => g.key === 'critical').verdicts;

// `aging`: the dealership's buckets; "aged" = the oldest bucket (id 4).
export const needsAttentionCounts = (units, openTaskStocks, aging = DEFAULT_AGING) => {
    const verdictCounts = countVerdicts(units);
    return {
        aged: units.filter((u) => unitAgingBucketId(u, aging) === OLDEST_BUCKET_ID).length,
        highRisk: units.filter((u) => u.RiskTier === 'Red').length,
        liveQuotes: countQualifyingQuotes(units),
        unitIssues: UNIT_ISSUE_VERDICTS.reduce((s, v) => s + (verdictCounts[v] || 0), 0),
        // The verdicts a "unit issues" click selects: the ones present.
        unitIssueVerdicts: UNIT_ISSUE_VERDICTS.filter((v) => verdictCounts[v] > 0),
        openTasks: openTaskStocks ? units.filter((u) => openTaskStocks.has(String(u.StockNumber))).length : 0,
    };
};

export const sameSet = (set, list) => set.size === list.length && list.every((v) => set.has(v));

// ── Drill-down URL params (D14: links from the Branches page) ────────────
// ?aging=4 (bucket ids, a list with commas: 1,4; the legacy aliases 0-30,
// 31-90, 91-180, 180+ and full bucket names keep old links working; each
// maps to an id), ?risk=High|Medium|Low, ?interest=Hot|Warm|Cold|No Interest,
// ?signal=<verdict>[,<verdict>], ?sort=gap.

export const DRILL_PARAMS = Object.freeze(['aging', 'risk', 'interest', 'signal']);

const RISK_ALIASES = { high: 'Red', red: 'Red', medium: 'Yellow', yellow: 'Yellow', low: 'Green', green: 'Green' };
const HEAT_VALUES = ['Hot', 'Warm', 'Cold', 'No Interest'];

const parseList = (raw, norm) => [...new Set((raw || '').split(',').map(norm).filter(Boolean))];

const parseOne = (name, raw, buckets = DEFAULT_AGING.buckets) => {
    if (raw === null || raw === undefined || raw === '') return null;
    if (name === 'aging') {
        // A bare "+" in a query string reads back as a space: "180 " → 180+.
        const list = parseList(raw, (v) => agingIdOf(v, buckets));
        return list.length ? list : null;
    }
    if (name === 'risk') return RISK_ALIASES[raw.trim().toLowerCase()] || null;
    if (name === 'interest') return HEAT_VALUES.find((h) => h.toLowerCase() === raw.trim().toLowerCase()) || null;
    if (name === 'signal') {
        const list = parseList(raw, (v) => {
            const s = v.trim().toLowerCase();
            return ALL_VERDICTS.find((verdict) => verdict.toLowerCase() === s || verdictLabel(verdict).toLowerCase() === s) || null;
        });
        return list.length ? list : null;
    }
    return null;
};

// { agingBucket?, riskTier?, buyerHeat?, verdicts? } for the params that
// parse, or null when none does.
export const parseDrillParams = (search, buckets = DEFAULT_AGING.buckets) => {
    const params = new URLSearchParams(search);
    const out = {};
    const aging = parseOne('aging', params.get('aging'), buckets);
    const risk = parseOne('risk', params.get('risk'));
    const interest = parseOne('interest', params.get('interest'));
    const signal = parseOne('signal', params.get('signal'));
    if (aging) out.agingBucket = aging;
    if (risk) out.riskTier = risk;
    if (interest) out.buyerHeat = interest;
    if (signal) out.verdicts = signal;
    return Object.keys(out).length ? out : null;
};

export const parseSortParam = (search) => (
    new URLSearchParams(search).get('sort') === 'gap' ? { ...GAP_SORT } : null
);

const sameList = (a, b) => a.length === b.length && a.every((v) => b.includes(v));

// Remove each drill param whose filter no longer says what the param says.
export const dropStaleDrillParams = (params, filters, verdicts, buckets = DEFAULT_AGING.buckets) => {
    const checks = {
        aging: (v) => sameList(v, normalizeAgingSelection(filters.agingBucket, buckets)),
        risk: (v) => filters.riskTier === v,
        interest: (v) => filters.buyerHeat === v,
        signal: (v) => sameList(v, [...verdicts]),
    };
    DRILL_PARAMS.forEach((name) => {
        if (!params.has(name)) return;
        const value = parseOne(name, params.get(name), buckets);
        if (value === null || !checks[name](value)) params.delete(name);
    });
};
